import { SubmitButton } from 'components/common/buttons/SubmitButton';
import EditPdfForm from 'components/Pdf/EditPdfForm';
import Api from 'services/api';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { fetchGeneratedInvoices, invoicesSelector, updateInvoice } from 'slices/invoiceSlice';

const toMinutes = (value, unit) => {
    const numeric = Number(value || 0);
    if (!Number.isFinite(numeric)) {
        return 0;
    }
    return unit === 'hour' ? numeric * 60 : numeric;
};

const buildEndTime = (startDate, startTime, durationValue, durationUnit) => {
    if (!startDate || !startTime || !durationValue) {
        return '';
    }
    const startMoment = moment(`${startDate} ${startTime}`, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm']);
    if (!startMoment.isValid()) {
        return '';
    }
    const minutes = toMinutes(durationValue, durationUnit);
    return startMoment.add(minutes, 'minutes').format('HH:mm');
};

const recalculateAppointment = (item) => {
    const scheduledDate = item.scheduledDate || '';
    const scheduledStartTime = item.scheduledStartTime || '';
    const scheduledDuration = item.scheduledDuration || 0;
    const scheduledDurationUnit = item.scheduledDurationUnit || 'minute';
    const scheduledEndTime = buildEndTime(scheduledDate, scheduledStartTime, scheduledDuration, scheduledDurationUnit);

    const actualDate = item.actualDate || scheduledDate;
    const actualStartTime = item.actualStartTime || '';
    const actualDurationMinutes = Number(item.actualDurationMinutes || 0);
    const actualEndTime = buildEndTime(actualDate, actualStartTime, actualDurationMinutes, 'minute');

    const minScheduledMinutes = toMinutes(scheduledDuration, scheduledDurationUnit);
    const totalBillableDuration = Math.max(actualDurationMinutes, minScheduledMinutes);

    const rate = Number(item.rate || 0);
    const rateUnit = item.rateUnit || 'hour';

    let baseCost = 0;
    if (rateUnit === 'minute') {
        baseCost = totalBillableDuration * rate;
    } else {
        baseCost = (totalBillableDuration / 60) * rate;
    }

    const mileage = Number(item.mileage || 0);
    const travel = Number(item.travel || 0);
    const rushFee = Number(item.rushFee || 0);
    const discount = Number(item.discount || 0);

    const subtotal = baseCost + mileage + travel;
    const discountAmount = subtotal * (discount / 100);
    const totalAmount = Math.max(0, subtotal - discountAmount) + rushFee;

    return {
        ...item,
        scheduledEndTime,
        actualEndTime,
        totalBillableDuration,
        totalAmount,
    };
};

const recalculateSpecialProject = (item) => {
    const rate = Number(item.rate || 0);
    const formatting = Number(item.formatting || 0);
    const rushFee = Number(item.rush_fee || 0);
    const wordsCount = Number(item.total_words_count || 0);

    const baseCost = rate * wordsCount;
    const amount = baseCost + formatting + rushFee;

    return {
        ...item,
        amount,
    };
};

const buildInitialState = (invoiceData) => {
    const appointments = (invoiceData?.client?.appointments || []).map((appointment) => {
        const appointmentDetails = appointment?.appointment_details || {};
        const appointmentAssign = appointment?.appointment_assign || {};
        const customPricing = appointment?.appointment_custom_pricing || {};
        const invoice = appointment?.invoice || {};
        const scheduledDate = appointment?.date || invoiceData?.billing_date || '';
        const scheduledStartTime = appointment?.start_time || '';
        const scheduledDuration = appointment?.estimated_duration || invoice?.min_duration || 60;
        const scheduledDurationUnit = appointment?.estimated_duration_unit || invoice?.duration_unit || 'minute';
        const actualDate = appointmentAssign?.checkin_date || scheduledDate;
        const actualStartTime = appointmentAssign?.checkin_time || '';
        const actualDurationMinutes = invoice?.extra_duration ?? toMinutes(scheduledDuration, scheduledDurationUnit);
        const dynamicFields = (() => {
            try {
                const parsed = appointmentDetails?.dynamic_fields ? JSON.parse(appointmentDetails.dynamic_fields) : [];
                return Array.isArray(parsed) ? parsed : [];
            } catch (error) {
                return [];
            }
        })();

        const rawItem = {
            id: appointment?.id,
            appid: appointment?.appid || '',
            language_id: appointment?.language?.id || '',
            languageName: appointment?.language?.name || '',
            subject: appointment?.subject || 'general',
            type: appointment?.type || 'In Person',
            facility_id: appointmentDetails?.facility_id || '',
            facilityName: appointmentDetails?.facility?.facility_name || '',
            scheduledDate,
            scheduledStartTime,
            scheduledDuration,
            scheduledDurationUnit,
            actualDate,
            actualStartTime,
            actualDurationMinutes,
            interpreterCode: appointment?.interpreter?.code || '',
            comments: appointmentAssign?.comments || '',
            requesterName: appointmentDetails?.requester_name || '',
            requesterEmail: appointmentDetails?.requester_email || '',
            requesterPhone: appointmentDetails?.requester_phone || '',
            notes: appointmentAssign?.notes || '',
            rate: invoice?.rate ?? '',
            rateUnit: invoice?.rate_unit || 'hour',
            rushFee: invoice?.rush_fee || 0,
            mileage: customPricing?.mileage_total ?? customPricing?.mileage ?? 0,
            travel: customPricing?.travel_total ?? customPricing?.travel ?? 0,
            discount: customPricing?.discount ?? 0,
            customPricingValue: customPricing?.appointment_total ?? customPricing?.sub_total ?? 0,
            totalAmount: invoice?.total_amount ?? 0,
            dynamicFields,
        };

        return recalculateAppointment(rawItem);
    });

    const specialProjects = (invoiceData?.client?.special_project || []).map((project) => {
        const rawItem = {
            translation_invoice_id: project?.id,
            projectNumber: project?.translation?.transid || '',
            project: project?.translation,
            created_at: project?.translation?.created_at || project?.created_at || '',
            source_language_id: project?.translation?.source_language_id || '',
            target_language_id: project?.translation?.translation_target_languages?.[0]?.language_id || '',
            sub_account_id: project?.translation?.subclient_id || project?.translation?.subclient?.id || '',
            subAccountName: project?.translation?.subclient?.subaccount_name || project?.translation?.subclient?.name || '',
            department_id: project?.translation?.translation_details?.department_id || project?.translation?.translation_details?.department?.id || '',
            departmentName: project?.translation?.translation_details?.department?.department_name || '',
            interpreterId: project?.interpreter?.id || '',
            vendorName: project?.interpreter
                ? `${project.interpreter.first_name || ''} ${project.interpreter.last_name || ''}`.trim()
                : '',
            rate: project?.rate ?? 0,
            formatting: project?.formatting ?? 0,
            rush_fee: project?.rush_fee ?? 0,
            amount: project?.amount ?? 0,
            current_phase: project?.translation?.translation_details?.current_phase || '',
            total_words_count: project?.translation?.total_words_count ?? 0,
            requesterName: project?.translation?.requester_name || '',
            requesterEmail: project?.translation?.translation_details?.requester_email || '',
            requesterPhone: project?.translation?.translation_details?.requester_phone || '',
        };

        return recalculateSpecialProject(rawItem);
    });

    return { appointments, special_projects: specialProjects };
};

const EditPdfView = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const appointmentTypeOptions = [
        { label: t('invoiceEditView.inPerson'), value: 'In Person' },
        { label: 'VRI Pre Scheduled', value: 'VRI Pre Scheduled' },
        { label: 'VRI On Demand', value: 'VRI On Demand' },
        { label: t('invoiceEditView.telephonic'), value: 'OPI Pre Scheduled' },
        { label: t('invoiceEditView.opiOnDemand'), value: 'OPI On Demand' },
    ];

    const modalityOptions = [
        { label: t('invoiceEditView.general'), value: 'general' },
        { label: t('invoiceEditView.medical'), value: 'medical' },
        { label: t('invoiceEditView.legal'), value: 'legal' },
        { label: t('invoiceEditView.court'), value: 'court' },
    ];

    const commentOptions = [
        { label: t('invoiceEditView.admissionIntake'), value: 'Admission/Intake' },
        { label: t('invoiceEditView.discharge'), value: 'Discharge' },
        { label: t('invoiceEditView.informedConsent'), value: 'Informed Consent' },
        { label: t('invoiceEditView.serviceDecline'), value: 'Service Decline' },
        { label: t('invoiceEditView.other'), value: 'Other' },
    ];
    const [buttonLoader, setButtonLoader] = useState(false);
    const [languages, setLanguages] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [formState, setFormState] = useState({ appointments: [], special_projects: [] });
    const { control, setValue } = useForm({ defaultValues: {} });

    const { generatedInvoices, loader } = useSelector(invoicesSelector);
    const generatedInvoice = generatedInvoices?.find((item) => item.id === Number(id));
    const invoiceNumber = generatedInvoice?.invoice_number;
    const clientData = generatedInvoice?.client || generatedInvoice;

    useEffect(() => {
        dispatch(fetchGeneratedInvoices(1, { invoice_id: id }));
    }, [dispatch, id]);

    useEffect(() => {
        let isMounted = true;

        const loadOptions = async () => {
            try {
                const languageResponse = await Api.fetchLanguages(1, { pageLength: 1000 });
                if (isMounted) {
                    setLanguages(Array.isArray(languageResponse?.data?.data?.data) ? languageResponse.data.data.data : []);
                }
            } catch (error) {
                console.error(error);
            }

            try {
                const clientId = generatedInvoice?.client?.id || generatedInvoice?.client_id || clientData?.id;
                let facilityResponse = null;

                if (clientId) {
                    facilityResponse = await Api.fetchFacilitiesByTypeId(clientId);
                }

                if (!facilityResponse || facilityResponse?.status >= 400) {
                    facilityResponse = await Api.fetchFacilities(1, { pageLength: 1000 });
                }

                if (isMounted) {
                    const nextFacilities = Array.isArray(facilityResponse?.data?.data)
                        ? facilityResponse.data.data
                        : Array.isArray(facilityResponse?.data)
                            ? facilityResponse.data
                            : [];

                    setFacilities(nextFacilities);
                }
            } catch (error) {
                console.error(error);
            }
        };

        loadOptions();
        return () => {
            isMounted = false;
        };
    }, [clientData]);

    useEffect(() => {
        if (generatedInvoice) {
            const nextState = buildInitialState(generatedInvoice);
            setFormState(nextState);
        }
    }, [generatedInvoice]);

    useEffect(() => {
        (formState.appointments || []).forEach((appointment, index) => {
            setValue(`appointment_${index}_scheduledStartTime`, appointment.scheduledStartTime || '');
            setValue(`appointment_${index}_scheduledEndTime`, appointment.scheduledEndTime || '');
            setValue(`appointment_${index}_actualStartTime`, appointment.actualStartTime || '');
            setValue(`appointment_${index}_actualEndTime`, appointment.actualEndTime || '');
        });
    }, [formState.appointments, setValue]);

    // Old code
    // const totalBill = useMemo(() => {
    //     const total = (formState.appointments || []).reduce((sum, item) => sum + Number(item.totalAmount || 0), 0);
    //     return total.toFixed(2);
    // }, [formState.appointments]);

    // New Code
    const totalBill = useMemo(() => {
        const appointmentsTotal = (formState.appointments || []).reduce(
            (sum, item) => sum + Number(item.totalAmount || 0),
            0
        );
        const specialProjectsTotal = (formState.special_projects || []).reduce(
            (sum, project) => sum + Number(project.amount || 0),
            0
        );
        return (appointmentsTotal + specialProjectsTotal).toFixed(2);
    }, [formState.appointments, formState.special_projects]);

    const fetchRatesForFieldChange = async (index, item) => {
        const clientId = generatedInvoice?.client?.id || generatedInvoice?.client_id || clientData?.id;
        if (!clientId || !item.language_id || !item.scheduledStartTime) {
            return;
        }

        // const endTime = buildEndTime(item.scheduledDate, item.scheduledStartTime, item.scheduledDuration, item.scheduledDurationUnit);
        const endTime = buildEndTime(item.actualDate, item.actualStartTime, item.actualDurationMinutes, 'minute');
        try {
            const res = await Api.fetchClientRates({
                client_id: clientId,
                language_id: item.language_id,
                start_time: item.actualStartTime,
                end_time: endTime || item.actualStartTime,
                type: item.type,
                subject: item.subject,
            });

            if (res?.status === 200 && res?.data?.data?.found) {
                const fetchedRate = res.data.data.rate;
                setFormState((prevState) => {
                    const nextAppointments = [...prevState.appointments];
                    if (!nextAppointments[index]) return prevState;
                    const updated = recalculateAppointment({
                        ...nextAppointments[index],
                        rate: fetchedRate,
                    });
                    nextAppointments[index] = updated;
                    return { ...prevState, appointments: nextAppointments };
                });
            }
        } catch (err) {
            console.error('Failed to fetch client rate', err);
        }
    };

    const fetchRateForSpecialProjectChange = async (index, item) => {
        const clientId = generatedInvoice?.client?.id || generatedInvoice?.client_id || clientData?.id;
        const targetLanguageId = item.target_language_id ?? item.project?.translation_target_languages?.[0]?.language_id;

        if (!clientId || !targetLanguageId) {
            return;
        }

        try {
            const res = await Api.fetchTranslationPricing(clientId, [targetLanguageId]);
            const totals = res?.data?.data?.totals || {};
            const phase = item.current_phase?.value ?? item.current_phase;
            const rateField = {
                review: 'review_rate',
                audio_recording: 'audio_recording_rate',
                transcription: 'transcription_rate',
                other: 'transcription_rate',  // other uses transcription rate
                editing: 'editing_rate',
            }[phase] || 'translation_rate';
            const fetchedRate = Number(totals[rateField] ?? 0);

            if (fetchedRate > 0) {
                setFormState((prevState) => {
                    const nextProjects = [...(prevState.special_projects || [])];
                    if (!nextProjects[index]) return prevState;
                    const updated = recalculateSpecialProject({
                        ...nextProjects[index],
                        rate: fetchedRate,
                    });
                    nextProjects[index] = updated;
                    return { ...prevState, special_projects: nextProjects };
                });
            }
        } catch (err) {
            console.error('Failed to fetch special project rate', err);
        }
    };

    const updateAppointmentField = (index, field, value, extra = {}) => {
        setFormState((prevState) => {
            const nextAppointments = [...prevState.appointments];
            if (!nextAppointments[index]) {
                return prevState;
            }

            const currentItem = nextAppointments[index];
            let nextItem = { ...currentItem, [field]: value, ...extra };

            nextItem = recalculateAppointment(nextItem);
            nextAppointments[index] = nextItem;

            // if (['language_id', 'subject', 'type', 'actualStartTime', 'actualDurationMinutes'].includes(field)) {
            if (['language_id', 'subject', 'type'].includes(field)) {
                setTimeout(() => fetchRatesForFieldChange(index, nextItem), 100);
            }

            return { ...prevState, appointments: nextAppointments };
        });
    };

    const updateSpecialProjectField = (index, field, value, extra = {}) => {
        setFormState((prevState) => {
            const nextProjects = [...(prevState.special_projects || [])];
            if (!nextProjects[index]) return prevState;

            let nextItem = {
                ...nextProjects[index],
                [field]: value,
                ...extra,
            };

            nextItem = recalculateSpecialProject(nextItem);
            nextProjects[index] = nextItem;

            if (['source_language_id', 'target_language_id', 'current_phase'].includes(field)) {
                setTimeout(() => fetchRateForSpecialProjectChange(index, nextItem), 100);
            }

            return { ...prevState, special_projects: nextProjects };
        });
    };

    const handleSubmit = () => {
        if (!generatedInvoice?.id) {
            toast.error('Invoice not found.', { className: 'errorToast' });
            return;
        }

        setButtonLoader(true);
        const payload = {
            invoice_id: generatedInvoice.id,
            appointments: formState.appointments.map((item) => ({
                appointment_id: item.id,
                language_id: item.language_id,
                subject: item.subject,
                type: item.type,
                date: item.scheduledDate,
                start_time: item.scheduledStartTime,
                end_time: item.scheduledEndTime,
                estimated_duration: item.scheduledDuration,
                estimated_duration_unit: item.scheduledDurationUnit,
                checkin_date: item.actualDate,
                checkin_time: item.actualStartTime,
                checkout_date: item.actualDate,
                checkout_time: item.actualEndTime,
                actual_duration_minutes: item.actualDurationMinutes,
                total_billable_duration: item.totalBillableDuration,
                facility_id: item.facility_id,
                comments: item.comments,
                requester_name: item.requesterName,
                requester_email: item.requesterEmail,
                requester_phone: item.requesterPhone,
                notes: item.notes,
                interpreter_code: item.interpreterCode,
                rate: item.rate,
                rate_unit: item.rateUnit,
                rush_fee: Number(item.rushFee || 0),
                mileage: Number(item.mileage || 0),
                travel: Number(item.travel || 0),
                discount: Number(item.discount || 0),
                custom_pricing: Number(item.totalAmount || 0),
                total_amount: Number(item.totalAmount || 0),
            })),
            special_projects: (formState.special_projects || []).map((project) => ({
                translation_invoice_id: project.translation_invoice_id,
                rate: Number(project.rate || 0),
                formatting: Number(project.formatting || 0),
                rush_fee: Number(project.rush_fee || 0),
                total_words_count: Number(project.total_words_count || 0),
                amount: Number(project.amount || 0),
                requester_name: project.requesterName,
                requester_email: project.requesterEmail,
                requester_phone: project.requesterPhone,
                created_at: project.created_at,
                source_language_id: project.source_language_id,
                target_language_id: project.target_language_id,
                sub_account_id: project.sub_account_id,
                department_id: project.department_id,
                current_phase: project.current_phase,
            })),
        };

        dispatch(updateInvoice(payload, generatedInvoice.id, (success, message) => {
            setButtonLoader(false);
            if (success) {
                toast.success(message || 'Invoice updated successfully.', { className: 'successToast' });
                navigate('/invoices/pdf-view/' + generatedInvoice.id);
            } else {
                toast.error(message || 'Invoice update failed.', { className: 'errorToast' });
            }
        }));
    };

    return (
        <div className="content">
            <div className="row_savebtn">
                <div className="d-flex align-items-center gap-2">
                    <button className="main_heading back_button" onClick={() => navigate(-1)}>
                        <i className="fa-solid fa-arrow-left"></i> {t('common.back')}
                    </button>
                </div>
                <SubmitButton
                    handleSubmit={handleSubmit}
                    buttonType="button"
                    loader={buttonLoader}
                    btnText={t('invoiceEditView.saveChanges')}
                />
            </div>

            <div className="view_pdf_main">
                <EditPdfForm
                    data={generatedInvoice}
                    loader={loader}
                    type={generatedInvoice?.invoice_type}
                    formState={formState}
                    updateAppointmentField={updateAppointmentField}
                    updateSpecialProjectField={updateSpecialProjectField}
                    languages={languages}
                    facilities={facilities}
                    invoiceNumber={invoiceNumber}
                    totalBill={totalBill}
                    appointmentTypeOptions={appointmentTypeOptions}
                    modalityOptions={modalityOptions}
                    commentOptions={commentOptions}
                    control={control}
                    clientId={generatedInvoice?.client?.id || generatedInvoice?.client_id || clientData?.id}
                />
            </div>
            <div className="row_savebtn">
                <div></div>
                <SubmitButton
                    handleSubmit={handleSubmit}
                    buttonType="button"
                    loader={buttonLoader}
                    btnText={t('invoiceEditView.saveChanges')}
                />
            </div>
        </div>
    );
};

export default EditPdfView;